import { createFileRoute, Link } from "@tanstack/react-router";
import { jsonLdScript, serviceSchema, breadcrumbSchema, pageHead } from "@/lib/seo";
import { Topbar } from "@/components/calv/Topbar";
import { Footer } from "@/components/calv/Footer";
import { LogoReferences } from "@/components/calv/LogoReferences";
import { Hero } from "@/components/calv/Hero";
import { Section } from "@/components/calv/Section";
import { ProcessFlow } from "@/components/calv/ProcessFlow";
import { Capabilities } from "@/components/calv/Capabilities";
import { Callout } from "@/components/calv/Callout";
import { CTABlock } from "@/components/calv/CTABlock";
import { RevealOnScroll } from "@/components/calv/RevealOnScroll";
import { RelatedServices } from "@/components/calv/RelatedServices";
import { relatedBySource } from "@/lib/related-services-data";

export const Route = createFileRoute("/leistungen/conversion-rate-optimierung")({
  head: () => {
    const h = pageHead("conversionRateOptimization", "de");
    return {
      meta: h.meta,
      links: h.links,
      scripts: [
        jsonLdScript(serviceSchema({
          name: "Conversion-Rate-Optimierung Agentur",
          description:
            "CRO-Agentur mit Forschungs-Disziplin: quantitative Funnel-Analyse, qualitative Insights, statistisch saubere Tests. Für E-Commerce, B2B und SaaS.",
          url: "/leistungen/conversion-rate-optimierung",
          serviceType: "ConversionRateOptimization",
        })),
        jsonLdScript(breadcrumbSchema([
          { name: "Start", path: "/" },
          { name: "Leistungen", path: "/leistungen" },
          { name: "Conversion-Rate-Optimierung", path: "/leistungen/conversion-rate-optimierung" },
        ])),
      ],
    };
  },
  component: CROPage,
});

const painPoints = [
  {
    title: `„Wir testen einfach mal"`,
    body: `Ohne Hypothese und ohne Forschungsbasis ist ein Test wertlos — selbst wenn er ein Ergebnis liefert. Was wurde gelernt? Warum hat es funktioniert? Lässt sich das Ergebnis auf andere Bereiche übertragen? Tests ohne Hypothese sind Glücksspiel mit Werbe-Budget. Wir starten jeden Test mit einer dokumentierten Hypothese: „Wir vermuten, dass X passiert, weil Y, gemessen über Z."`,
  },
  {
    title: `Sample-Size wird nicht berechnet`,
    body: `Ein Test mit zu kleinem Sample ist statistisch wertlos. „Variante B ist 12 % besser nach 100 Conversions" ist kein Ergebnis, das ist Zufall. Eine seriöse CRO-Praxis berechnet vor jedem Test die nötige Sample-Size auf Basis von Baseline-Conversion-Rate, Minimum Detectable Effect und gewünschtem Confidence-Level (typisch 95 %). Tests ohne diese Berechnung sind Theater.`,
  },
  {
    title: `Optimierung auf Mikro-KPIs statt Wirtschaftlichkeit`,
    body: `Click-Through-Rate auf einem Banner um 30 % gesteigert — und der Umsatz bleibt gleich. Add-to-Cart-Rate verbessert — aber Checkout-Conversion verschlechtert. Optimierung auf einzelne Metriken ohne Funnel-Validierung führt zu Verschiebungen, nicht zu Wirtschaftlichkeit. Wir messen immer auf den End-Conversion-Wert, nicht auf Zwischen-Schritte.`,
  },
  {
    title: `Test-Pipeline ohne Priorisierung`,
    body: `„Wir testen jetzt erst mal die Headline. Dann das Bild. Dann den Button." Ohne Priorisierung nach wirtschaftlichem Hebel werden 80 % der Test-Kapazität auf 20 % des Wirkungs-Potenzials verschwendet. Wir priorisieren mit ICE- oder PIE-Frameworks: Impact, Confidence, Ease — gewichtet nach Mandate-Realität.`,
  },
];

const triade = [
  {
    eyebrow: "Quantitativ",
    title: "Das WO",
    body: "Funnel-Analyse über GA4 und BigQuery. Heatmaps und Click-Tracking über Microsoft Clarity oder Hotjar. Cohort-Analyse für Wiederkehr-Verhalten. Drop-Off-Analysen pro Funnel-Stufe. Output: konkrete Hotspots, wo wirtschaftlich Geld verloren geht."
  },
  {
    eyebrow: "Qualitativ",
    title: "Das WARUM",
    body: "Session-Recordings ausgewählter User-Sessions. Customer-Surveys auf strategischen Funnel-Punkten. User-Tests mit kleiner Stichprobe. Heuristik-Analyse nach Nielsen, Krug oder anderen etablierten UX-Frameworks. Output: Hypothesen, warum die Hotspots existieren."
  },
  {
    eyebrow: "Statistisch",
    title: "Das WAS",
    body: "Methodik passend zum Volumen: A/B-Tests mit korrekt berechneter Sample-Size, wo Volumen es zulässt. Bayesian Statistics bei mittlerem Volumen für validere Aussagen mit weniger Daten. Bei kleinem Volumen: Heuristik-basierte Validierung plus dokumentierte Best-Practice-Implementation statt unzuverlässiger Tests. Output: volumen-passende, validierte Verbesserungen."
  },
];

const pipeline = [
  { num: "01", title: "Funnel-Diagnose (3–5 Tage)", body: "Quantitative Analyse des bestehenden Funnels. GA4-Audit, Tracking-Validierung, Drop-Off-Identifikation pro Funnel-Stufe. Output: priorisierte Hotspot-Liste mit wirtschaftlichem Wirkungs-Potenzial." },
  { num: "02", title: "Forschung & Hypothesen-Bildung (1–2 Wochen)", body: "Qualitative Insights über Heatmaps, Session-Recordings, optional Customer-Surveys. Heuristik-Analyse nach UX-Frameworks. Pro Hotspot mindestens drei dokumentierte Hypothesen mit konkreter Mess-Definition." },
  { num: "03", title: "Test-Pipeline-Aufbau (1 Woche)", body: "ICE- oder PIE-Priorisierung der Hypothesen. Sample-Size-Berechnung pro Test. Test-Setup in VWO, Optimizely oder vergleichbarer Plattform. Tracking-Validierung vor Go-Live." },
  { num: "04", title: "Test-Durchführung (kontinuierlich, ab Woche 3–4)", body: `Wöchentlicher Test-Zyklus mit klarer Hypothesen-Pipeline. Statistische Signifikanz-Prüfung vor Test-Beendigung. Sequential Testing wo passend für kürzere Test-Dauern. Strikt kein Cherry-Picking, kein vorzeitiges Beenden bei „guten Zwischenständen".` },
  { num: "05", title: "Implementierung & Skalierung (parallel zu Phase 04)", body: `Erfolgreiche Tests werden in Produktion ausgerollt. Erkenntnisse fließen in nachfolgende Hypothesen-Bildung. Aufbau einer dokumentierten „Lessons-Learned"-Datenbank pro Mandate.` },
  { num: "06", title: "Customer-Lifecycle-Erweiterung (ab Monat 3)", body: `Tests werden über den gesamten Funnel ausgeweitet — Ad-Klick, Landing-Page, Produkt-Detail, Cart, Checkout, Post-Purchase, Retention. CRO ist kein „Landing-Page-only"-Spiel, sondern Customer-Journey-Optimierung.` },
];

const capabilities = [
  { name: "Funnel-Diagnose & Hotspot-Mapping", detail: "GA4, BigQuery, Drop-Off-Analyse" },
  { name: "Heuristik- & Heatmap-Analyse", detail: "Nielsen, Krug, Clarity, Hotjar" },
  { name: "A/B- & Multivariate-Tests", detail: "Sample-Size, Bayesian, Sequential" },
  { name: "Customer-Lifecycle-Testing", detail: "Ad-Klick bis Retention" },
  { name: "UX-Research & Customer-Insights", detail: "Surveys, User-Tests, VoC" },
  { name: "Tracking-Hygiene als Voraussetzung", detail: "Server-side, Enhanced Conversions" },
  { name: "Wirtschaftliche Validierung", detail: "End-Conversion-Wert, Deckungsbeitrag" },
  { name: "Test-Dokumentation & Lessons-Learned", detail: "Wissens-Akkumulation" },
];

const volumeClasses = [
  {
    eyebrow: "KLASSE 1 — KLEINES VOLUMEN",
    badge: "CALVARIUS-SCHWERPUNKT",
    title: "200–1.000 monatliche Conversions",
    body: "Hier liegen die meisten KMU-Setups, die wir betreuen. Wir arbeiten primär mit qualitativen Methoden: Customer-Research, User-Tests mit kleiner Stichprobe, Heuristik-Analyse nach UX-Frameworks, Heatmap-Diagnose über Microsoft Clarity oder Hotjar. A/B-Tests setzen wir gezielt dort ein, wo wir substanzielle Effekte erwarten (15–20 % oder mehr) und wo Test-Zeiträume von 4–8 Wochen praktikabel sind. Realistische Verbesserungen: 8–25 % innerhalb von 6–9 Monaten — primär durch saubere Forschung, Best-Practice-Implementation und gezielte Tests, nicht durch Test-Pipeline-Volumen.",
  },
  {
    eyebrow: "KLASSE 2 — MITTLERES VOLUMEN",
    badge: "HÄUFIGER CALVARIUS-BEREICH",
    title: "1.000–5.000 monatliche Conversions",
    body: "Hier wird klassisches A/B-Testing wirtschaftlich tragbar — aber mit Anpassungen gegenüber Enterprise-Methodik. Wir nutzen Bayesian Statistics statt Frequentist (kommt mit weniger Daten zu validen Aussagen), Sequential Testing für kürzere Test-Dauern, klare Fokussierung auf große erwartete Effekte statt feiner Optimierung. Realistische Verbesserungen: 10–30 % innerhalb von 6 Monaten, mit 2–4 parallel laufenden Tests.",
  },
  {
    eyebrow: "KLASSE 3 — SEHR KLEINES VOLUMEN",
    badge: null,
    title: "Unter 200 monatliche Conversions",
    body: "Hier sind klassische A/B-Tests nicht statistisch valide — auch nicht mit Bayesian Statistics. Wir arbeiten dann ausschließlich qualitativ: Heuristik-Analyse, Customer-Surveys, User-Tests, Best-Practice-Refactoring auf Basis dokumentierter Industrie-Standards. Das sagen wir vor dem Mandate ehrlich — nicht, dass wir Mandate ablehnen, sondern dass wir die Methodik an die Realität anpassen. Messbare Effekte in 4–8 Wochen.",
  },
  {
    eyebrow: "KLASSE 4 — HOHES VOLUMEN",
    badge: null,
    title: "5.000+ monatliche Conversions",
    body: "Enterprise-Bereich, in dem klassisches A/B-Testing mit 95 % Confidence-Level voll skalierbar ist. Multivariate Tests möglich. Test-Dauern von 2–4 Wochen pro Test. Test-Pipeline mit 3–5 parallel laufenden Tests. Calvarius betreut auch Mandate dieser Größe — aber es ist nicht der Schwerpunkt unseres Mandate-Mix, sondern eine weitere Volumen-Klasse, die wir methodisch sauber abdecken.",
  },
];

const benefits = [
  { strong: "Forschungs-Substanz vor jeder Aktion.", body: "Wir testen nicht ins Blaue — jede CRO-Aktion startet mit einer dokumentierten Hypothese auf Basis quantitativer und qualitativer Forschung." },
  { strong: "Statistische Disziplin als Pflicht-Bestandteil.", body: "Sample-Size-Berechnung, korrekte Confidence-Levels, kein Cherry-Picking, kein vorzeitiges Beenden. Echte Wissenschaftlichkeit, nicht Test-Theater." },
  { strong: "Wirtschaftliche Erdung statt Mikro-KPI-Optimierung.", body: "Wir messen auf End-Conversion-Wert und Deckungsbeitrag, nicht auf Click-Through-Rate oder Add-to-Cart-Rate." },
  { strong: "Customer-Lifecycle-Sicht statt Landing-Page-Tunnel.", body: "CRO über den gesamten Funnel, weil Wirkung dort entsteht, wo Reibung sitzt — und Reibung sitzt selten nur auf einer Landing-Page." },
  { strong: "Ehrliche Volumen-Realismus.", body: "Wir sagen vor dem Mandate, ob CRO bei Ihrem Traffic-Volumen wirtschaftlich tragbar ist. Falls nicht: andere Hebel statt CRO." },
  { strong: "Wissens-Akkumulation pro Mandate.", body: "Test-Dokumentation und Lessons-Learned-Datenbank — Erkenntnisse aus früheren Tests fließen in spätere Hypothesen ein." },
];

function CROPage() {
  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          variant="short"
          eyebrow="Leistungen · Conversion-Rate-Optimierung"
          headline={<>Conversion-Rate-Optimierung Agentur — <em>Hypothesen-Disziplin,</em> nicht A/B-Test-Theater.</>}
          lead="Quantitative Daten, qualitative Insights und statistisch saubere Validierung — als zusammenhängendes System, nicht als Knopfdruck-Optimierung. Calvarius arbeitet als CRO-Agentur für E-Commerce, B2B-Leadgenerierung und SaaS-Trial-Sign-ups, mit klarer Forschungs-Disziplin vor jeder Aktion."
          metaStrip={["Forschung vor Test", "Statistische Disziplin", "Wirtschaftlich geerdet"]}
          breadcrumbs={[{ label: "Start", to: "/" }, { label: "Leistungen", to: "/leistungen" }, { label: "Conversion-Rate-Optimierung" }]}
        />

        <Section number="02" kicker="Standpunkt" headline={<>Forschungs-Disziplin <em>vor</em> Aktion.</>}>
          <RevealOnScroll>
            <div className="prose max-w-2xl text-[17px] leading-relaxed text-ink-2">
              <p className="mb-5">Wer eine Conversion-Rate-Optimierung-Agentur sucht, hat in der Regel eine kritische Beobachtung gemacht: Der Markt ist voll von Anbietern, die A/B-Tests verkaufen, ohne die Disziplin dahinter zu verstehen. „Mal sehen, ob grün besser konvertiert als rot" ist kein CRO — das ist Glücksspiel mit Werbe-Budget. Ein Test ohne Hypothese ist ein Test ohne Lerneffekt. Ein Test ohne ausreichendes Sample wird zum Zufalls-Ergebnis. Ein Test ohne wirtschaftliche Erdung optimiert auf Vanity-Metriken statt auf Deckungsbeitrag. Wer CRO ernst nimmt, arbeitet anders.</p>
              <p className="mb-5">Calvarius behandelt Conversion-Rate-Optimierung als Forschungs-Disziplin mit Aktionsfolge — nicht als Knopfdruck-Test-Werkstatt. Das bedeutet: Vor jedem Test steht eine Hypothese. Vor jeder Hypothese steht Forschung. Vor der Forschung steht eine klare Funnel-Diagnose. Wir wissen, wo im Funnel die wirtschaftlich relevanten Conversion-Verluste passieren — und priorisieren dort, wo der wirtschaftliche Hebel am größten ist. Eine 5-%-Verbesserung auf einer Produkt-Detail-Seite mit hohem Margen-Anteil ist mehr wert als eine 30-%-Verbesserung auf einer Newsletter-Anmeldung.</p>
              <p className="mb-5">Die methodische Triade, mit der wir arbeiten, ist nicht verhandelbar: quantitative Funnel-Analyse zeigt, <strong>wo</strong> Probleme sind. Qualitative Insights über Heatmaps, Session-Recordings und Customer-Research zeigen, <strong>warum</strong> Probleme entstehen. A/B-Tests mit korrekter Statistik validieren, <strong>was</strong> funktioniert. Wer eine dieser drei Ebenen weglässt, betreibt nicht CRO — sondern Bauchgefühl mit Test-Anstrich. Wir nutzen alle drei systematisch und in der richtigen Reihenfolge.</p>
              <p className="mb-5">Konkret heißt das: Wir arbeiten mit Plattform-Tools wie Microsoft Clarity, Hotjar, VWO und Optimizely — kombiniert mit GA4 und BigQuery-Export für saubere Datenbasis. Wir testen über den gesamten Customer-Lifecycle, nicht nur auf einzelnen Landing-Pages — Ad-Klick, Landing-Page, Produkt-Detail, Cart, Checkout, Post-Purchase, Retention. Und wir wählen die Methodik passend zum Volumen: Bei KMU-Setups mit 200–1.000 monatlichen Conversions arbeiten wir primär qualitativ — Customer-Research, Heuristik-Analyse, gezielte Tests bei großen erwarteten Effekten. Bei mittlerem Volumen mit Bayesian Statistics und Sequential Testing. Bei hohem Volumen mit klassischem A/B-Testing-Setup. Wer KMUs Enterprise-Methodik verkauft, verbrennt deren Budget.</p>
              <p>Für Shopify-Stores betrachten wir Conversion immer im Zusammenspiel mit der technischen Basis — Theme-Performance, Checkout-Anpassung und App-Auswahl entscheiden mit, wie viel ein Test überhaupt heben kann. Mehr dazu auf unserer Seite zur <Link to="/leistungen/onlineshop-entwicklung/shopify-agentur" className="text-cyan-deep underline-offset-4 hover:underline">Shopify-Agentur</Link>.</p>
            </div>
          </RevealOnScroll>
          <Callout>Ein Test ohne Hypothese ist Glücksspiel mit Werbe-Budget. Wir testen nicht ins Blaue.</Callout>
        </Section>

        <Section variant="tinted" number="03" kicker="Typische Fehler" headline={<>Wo CRO in der Praxis <em>systematisch scheitert.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {painPoints.map((p, i) => (
                <article key={i} className="bg-white p-7">
                  <div className="font-mono text-xs tracking-wider text-cyan-deep">FEHLER 0{i + 1}</div>
                  <h3 className="mt-3 font-display text-xl text-ink">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{p.body}</p>
                </article>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section number="04" kicker="Unsere Methodik" headline={<>Quantitativ, qualitativ, statistisch — als <em>zusammenhängendes</em> System.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-3">
              {triade.map((col, i) => (
                <div key={i} className="bg-white p-7">
                  <p className="eyebrow mb-3">{col.eyebrow}</p>
                  <h3 className="font-display text-xl text-ink">{col.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{col.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              Diese Triade ist nicht modular — alle drei Schichten sind Pflicht. Wer nur testet ohne Forschung, optimiert blind. Wer nur Heatmaps anschaut ohne Validierung, optimiert anekdotisch. Wer nur Funnel-Daten ohne qualitative Tiefe analysiert, sieht zwar das Wo, aber nicht das Warum. Erst die Kombination liefert systematische Verbesserung.
            </p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="05" kicker="Unser Vorgehen" headline={<>Wie ein CRO-Mandate bei uns <em>abläuft.</em></>}>
          <ProcessFlow steps={pipeline} />
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              Diese Pipeline läuft nicht linear, sondern zyklisch — jede Test-Erkenntnis fließt zurück in die Hypothesen-Bildung. Der wirtschaftliche Hebel wächst mit jeder Iteration, weil das Wissen über die spezifische Mandate-Audience wächst.
            </p>
          </RevealOnScroll>
        </Section>

        <Section number="06" kicker="Was wir liefern" headline={<>Capabilities im <em>CRO-Spektrum.</em></>}>
          <Capabilities items={capabilities} />
        </Section>

        <Section variant="tinted" number="07" kicker="Tooling" headline={<>Womit wir CRO operativ <em>steuern.</em></>}>
          <RevealOnScroll>
            <div className="grid gap-6 md:grid-cols-2">
              {[
                { title: "Quantitative Datenbasis", items: ["Google Analytics 4 mit BigQuery-Export", "Server-side Tracking via GTM-Server", "Funnel-Visualisierung in Looker Studio"] },
                { title: "Qualitative Insights", items: ["Microsoft Clarity (kostenlos, datenschutzkonform)", "Hotjar (für tiefere Heatmap- und Survey-Funktionen)", "Custom-Survey-Tools je nach Anforderung"] },
                { title: "A/B- und Multivariate-Testing", items: ["VWO — Hauptplattform für die meisten Mandate", "Optimizely — wo Enterprise-Funktionen oder Server-side-Testing nötig sind", "Convert.com als Mid-Market-Alternative", "Eigene Implementierungen via Feature Flags wo Plattform-Tools nicht reichen"] },
                { title: "Statistische Werkzeuge", items: ["Sample-Size-Calculator (intern, Calvarius-eigene Spreadsheets)", "Bayesian Statistics Toolkit für komplexere Auswertungen", "Sequential Testing-Frameworks für kürzere Test-Dauern"] },
              ].map((b) => (
                <div key={b.title} className="rounded-lg border border-rule bg-white p-6">
                  <p className="eyebrow mb-3">{b.title}</p>
                  <ul className="space-y-2 text-sm leading-relaxed text-ink-2">
                    {b.items.map((it) => (
                      <li key={it} className="flex gap-3"><span className="text-cyan" aria-hidden>●</span><span>{it}</span></li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <Section
          number="08"
          kicker="Was realistisch ist"
          headline={<>Volumen-Realismus — die richtige Methodik für <em>Ihren Setup.</em></>}
          caption="CRO ist nicht nur klassisches A/B-Testing. Die meisten Calvarius-Mandate sind KMUs aus dem E-Commerce-Bereich mit Monatsbudgets von 10.000–100.000 € — und damit Setups, in denen Enterprise-A/B-Test-Methodik wirtschaftlich nicht funktioniert. Wir arbeiten mit der Methodik, die zur Volumen-Klasse passt — nicht mit der, die in Konferenz-Vorträgen am beeindruckendsten klingt."
        >
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {volumeClasses.map((c, i) => (
                <article key={i} className="bg-white p-7">
                  <div className="font-mono text-xs tracking-wider text-cyan-deep">{c.eyebrow}</div>
                  <h3 className="mt-3 font-display text-xl text-ink">{c.title}</h3>
                  {c.badge && (
                    <p className="mt-2 font-mono text-[11px] tracking-wider text-ink-4">{c.badge}</p>
                  )}
                  <p className="mt-3 text-sm leading-relaxed text-ink-3">{c.body}</p>
                </article>
              ))}
            </div>
          </RevealOnScroll>
          <RevealOnScroll>
            <p className="mt-10 max-w-2xl text-[17px] leading-relaxed text-ink-2">
              In der Calvarius-Mandate-Realität fallen die meisten Setups in Klasse 1 oder 2 — KMU-Setups mit B2C-E-Commerce-Schwerpunkt, bei denen die Wahl der richtigen CRO-Methodik wirtschaftlich entscheidend ist. Wer KMUs Enterprise-Methodik verkauft, verbrennt deren Budget. Wer KMUs nichts anbietet, weil das Volumen für klassisches Testing nicht reicht, lässt wirtschaftlichen Hebel liegen. Wir machen weder das eine noch das andere — sondern liefern die Methodik, die zum Setup passt.
            </p>
          </RevealOnScroll>
        </Section>

        <Section variant="tinted" number="09" kicker="Was Sie gewinnen" headline={<>Was Sie von unserer <em>CRO-Disziplin</em> bekommen.</>}>
          <RevealOnScroll>
            <div className="grid gap-px overflow-hidden rounded-lg border border-rule bg-rule md:grid-cols-2">
              {benefits.map((b, i) => (
                <div key={i} className="bg-white p-6">
                  <p className="text-[15px] leading-relaxed text-ink-2"><strong className="text-ink">{b.strong}</strong> {b.body}</p>
                </div>
              ))}
            </div>
          </RevealOnScroll>
        </Section>

        <LogoReferences locale="de" number="10" />

        <RelatedServices
          locale="de"
          fromId="conversion-rate-optimierung"
          number="11"
          items={relatedBySource["conversion-rate-optimierung"]}
        />

        <Section number="12" kicker="Konkret werden">
          <CTABlock
            headline={<>CRO-Audit in 30 Minuten — <em>kostenlos</em> und unverbindlich.</>}
            body="In einem ersten Gespräch klären wir, ob Ihr Setup wirtschaftlich CRO-tragbar ist — also ob das Traffic-Volumen für valide Tests reicht und wo die wirkungsvollsten Hotspots liegen. Sie bekommen eine ehrliche Einordnung, auch wenn das heißt, dass CRO aktuell nicht der richtige Hebel ist."
            primary={{ label: "Gespräch vereinbaren", to: "/kontakt?topic=cro" }}
            secondary={{ label: "Alle Leistungen", to: "/leistungen" }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
